import { useEffect, useRef } from 'react';
import { Box, useTheme } from '@mui/material';
import katex from 'katex';
import 'katex/dist/katex.min.css';

/**
 * Normalize MathLive-specific LaTeX commands that KaTeX can't handle via macros.
 * \mleft/\mright must be string-replaced because KaTeX treats \left/\right as
 * special parser tokens — a macro redirect would not work.
 */
const normalizeMathLiveLatex = (latex) =>
    latex.replace(/\\mleft/g, '\\left').replace(/\\mright/g, '\\right');

const QuestionRender = ({ content, sx = {}, highlightId = null }) => {
    const theme = useTheme();
    const containerRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current || !content) return;

        // Ensure content is a string
        const contentStr = typeof content === 'string' ? content : String(content);

        // First decode HTML entities
        let processedContent = contentStr
            .replace(/&gt;/g, '>')
            .replace(/&lt;/g, '<')
            .replace(/&amp;/g, '&')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&nbsp;/g, ' ')
            .replace(/&deg;/g, '°');

        // Arrays to store LaTeX blocks and their placeholders
        const latexBlocks = [];
        const latexPlaceholders = [];

        // IMPORTANT: Extract delimited math blocks FIRST before standalone environments
        // This ensures \[...\] containing \begin{env} is captured as a whole unit

        // Extract display mode LaTeX \[...\] (may contain environments inside)
        processedContent = processedContent.replace(/\\\[([\s\S]*?)\\\]/g, (_, latex) => {
            const placeholder = `___LATEX_DISPLAY_${latexBlocks.length}___`;
            latexBlocks.push(latex);
            latexPlaceholders.push(placeholder);
            return placeholder;
        });

        // NOTE: $$...$$ and $...$ are intentionally NOT treated as math delimiters.
        // The math convention is \(...\) / \[...\] (hw AI prompts mandate it: "ALWAYS
        // use \( \) ... NEVER use $ or $$"). Treating $ as math collides with currency
        // (e.g. "$7,232"), so a literal $ renders as a dollar sign. Existing content was
        // migrated off $...$ (v2 tests, hw_questions); the question bank is empty.

        // Extract inline mode LaTeX \(...\)
        processedContent = processedContent.replace(/\\\(([\s\S]*?)\\\)/g, (_, latex) => {
            const placeholder = `___LATEX_INLINE_${latexBlocks.length}___`;
            latexBlocks.push(latex);
            latexPlaceholders.push(placeholder);
            return placeholder;
        });

        // Extract standalone environment blocks (array, tabular, matrix, align, etc.)
        // that are NOT already wrapped in delimiters
        processedContent = processedContent.replace(/\\begin\{([^}]+)\}([\s\S]*?)\\end\{\1\}/g, (match) => {
            const placeholder = `___LATEX_DISPLAY_${latexBlocks.length}___`;
            latexBlocks.push(match);
            latexPlaceholders.push(placeholder);
            return placeholder;
        });
        
        // Convert newlines to <br> tags
        processedContent = processedContent.replace(/\n/g, '<br>');
        
        // Remove <br> tags within <ul> blocks to preserve list formatting
        processedContent = processedContent.replace(/(<ul[\s\S]*?<\/ul>)/gi, (ulBlock) =>
            ulBlock.replace(/<br\s*\/?>/gi, '')
        );
        
        // Process custom image syntax {#image url}
        processedContent = processedContent.replace(
            /{#image (.+?)}/g,
            '<img src="$1" draggable="false" style="max-width: 100%; height: auto;"/>'
        );
        
        // Process center content syntax {#center}...{/center}
        processedContent = processedContent.replace(
            /(?:<br>)?{#center}(.+?){\/center}(?:<br>)?/g,
            '<div class="center-content q">$1</div>'
        );
        
        // Render all LaTeX blocks
        for (let i = 0; i < latexBlocks.length; i++) {
            try {
                const isDisplay = latexPlaceholders[i].includes('DISPLAY');

                // Clean up LaTeX: remove nested \(...\) delimiters from within environments
                // since content inside environments like array, matrix, etc. is already in math mode
                let cleanedLatex = latexBlocks[i];
                if (cleanedLatex.includes('\\begin{')) {
                    cleanedLatex = cleanedLatex
                        .replace(/\\\(/g, '')
                        .replace(/\\\)/g, '');
                }

                cleanedLatex = normalizeMathLiveLatex(cleanedLatex);

                const renderedHtml = katex.renderToString(cleanedLatex, {
                    displayMode: isDisplay,
                    throwOnError: false,
                    errorColor: '#cc0000',
                    strict: false,
                    trust: true,
                    macros: {
                        // Fix for negation symbols that don't render properly
                        "\\neq": "\\mathrel{\\char`≠}",
                        "\\ne": "\\mathrel{\\char`≠}",
                        // Number sets
                        "\\R": "\\mathbb{R}",
                        "\\N": "\\mathbb{N}",
                        "\\Z": "\\mathbb{Z}",
                        "\\Q": "\\mathbb{Q}",
                        "\\C": "\\mathbb{C}",
                        // Geometry - arcs and angles
                        "\\overparen": "\\overset{\\frown}{#1}",
                        "\\overarc": "\\overset{\\frown}{#1}",
                        "\\arc": "\\overset{\\frown}{#1}",
                        "\\wideparen": "\\overset{\\frown}{#1}",
                        "\\underparen": "\\underset{\\smile}{#1}",
                        // Units and symbols
                        "\\degree": "^{\\circ}",
                        "\\deg": "^{\\circ}",
                        "\\celsius": "^{\\circ}\\text{C}",
                        "\\fahrenheit": "^{\\circ}\\text{F}",
                        "\\ohm": "\\Omega",
                        // Common shortcuts
                        "\\congruent": "\\cong",
                        "\\nparallel": "\\nparallel",
                        // Vectors
                        "\\vect": "\\overrightarrow{#1}",
                        "\\ray": "\\overrightarrow{#1}",
                        // Line segments
                        "\\segment": "\\overline{#1}",
                        "\\seg": "\\overline{#1}",
                        // MathLive semantic commands
                        "\\exponentialE": "\\mathrm{e}",
                        "\\imaginaryI": "\\mathrm{i}",
                        "\\imaginaryJ": "\\mathrm{j}",
                        "\\differentialD": "\\mathrm{d}",
                        "\\capitalDifferentialD": "\\mathrm{D}",
                        "\\rd": "\\mathrm{d}",
                        "\\rD": "\\mathrm{D}",
                        // MathLive double-struck (blackboard bold) letters
                        "\\doubleStruckCapitalN": "\\mathbb{N}",
                        "\\doubleStruckCapitalR": "\\mathbb{R}",
                        "\\doubleStruckCapitalQ": "\\mathbb{Q}",
                        "\\doubleStruckCapitalZ": "\\mathbb{Z}",
                        "\\doubleStruckCapitalP": "\\mathbb{P}",
                        // MathLive script/gothic letters
                        "\\scriptCapitalE": "\\mathscr{E}",
                        "\\scriptCapitalH": "\\mathscr{H}",
                        "\\scriptCapitalL": "\\mathscr{L}",
                        "\\gothicCapitalC": "\\mathfrak{C}",
                        "\\gothicCapitalH": "\\mathfrak{H}",
                        "\\gothicCapitalI": "\\mathfrak{I}",
                        "\\gothicCapitalR": "\\mathfrak{R}",
                        // MathLive fractions
                        "\\nicefrac": "^{#1}\\!\\!/\\!_{#2}",
                        // Common alias
                        "\\infinity": "\\infty",
                    }
                });

                if (isDisplay) {
                    processedContent = processedContent.replace(
                        latexPlaceholders[i],
                        `<div class="math-display">${renderedHtml}</div>`
                    );
                } else {
                    processedContent = processedContent.replace(
                        latexPlaceholders[i],
                        `<span class="math-inline">${renderedHtml}</span>`
                    );
                }
            } catch (error) {
                console.error('KaTeX rendering error:', error);
                console.log('Problematic LaTeX:', latexBlocks[i]);
                // If rendering fails, show the original LaTeX with error styling
                processedContent = processedContent.replace(
                    latexPlaceholders[i],
                    `<span class="katex-error" style="color: #cc0000;" title="${error}">${latexBlocks[i]}</span>`
                );
            }
        }
        
        // Set the processed HTML content
        containerRef.current.innerHTML = processedContent;

        // Highlight the passage span(s) the current question refers to (ACT Reading `ref-N`).
        // Clear any prior highlight first so navigating between questions that share a passage
        // hands off cleanly. Use querySelectorAll (not getElementById/querySelector) because the
        // same `ref-N` id can legitimately appear on multiple spans within one passage.
        containerRef.current
            .querySelectorAll('.passage-highlight')
            .forEach((el) => el.classList.remove('passage-highlight'));

        if (highlightId && /^ref-\d+$/.test(highlightId)) {
            containerRef.current
                .querySelectorAll(`span[id="${highlightId}"]`)
                .forEach((el) => el.classList.add('passage-highlight'));
        }

    }, [content, highlightId]);

    if (!content) return null;

    return (
        <Box 
            ref={containerRef}
            sx={{
                '& .math-display': {
                    display: 'block',
                    textAlign: 'center',
                    margin: '1em 0',
                },
                '& .math-inline': {
                    display: 'inline',
                    verticalAlign: 'middle',
                },
                '& .katex': {
                    fontSize: '1.1em',
                },
                '& .katex-display': {
                    margin: '1em 0',
                },
                '& p': {
                    margin: '0.5em 0',
                },
                '& .center-content.q': {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    margin: '1em 0',
                    width: '100%',
                },
                '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                    display: 'block',
                    margin: '1em auto',
                },
                // ACT Reading: highlight the passage span(s) the current question references
                '& .passage-highlight': {
                    backgroundColor: theme.palette.mode === 'dark' ? '#78350f' : '#fef3c7',
                    padding: '2px 4px',
                    borderRadius: '10px',
                    transition: 'background-color 0.3s ease',
                },
                ...sx
            }}
        />
    );
};

export default QuestionRender;